import {
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';

const DetailRow = ({ label, value, isLink = false, isStatus = false, isTag = false, customTagClass = '' }) => {
  if (!value || (Array.isArray(value) && value.length === 0)) return null;

  const renderValue = (val) => {
    if (Array.isArray(val)) {
      return val.join(', ');
    }
    if (typeof val === 'string' || typeof val === 'number') {
      // Special handling for data layer values to capitalize first letter
      if (typeof val === 'string' && ['bronze', 'gold', 'silver', 'ingestion'].includes(val.toLowerCase())) {
        return val.charAt(0).toUpperCase() + val.slice(1).toLowerCase();
      }
      // Special handling for assetSource values
      if (typeof val === 'string') {
        switch (val.toLowerCase()) {
          case 'uc': return 'UC';
          case 'adls': return 'ADLS';
          case 'manual': return 'Manual';
          default: return val;
        }
      }
      return val;
    }
    if (typeof val === 'boolean') return val.toString();
    return '';
  };

  const getStatusColor = (status) => {
    if (status?.toLowerCase().includes('onboarded')) return { bg: '#e8f5e8', text: '#2e7d32' };
    if (status?.toLowerCase().includes('active')) return { bg: '#e8f5e8', text: '#2e7d32' };
    if (status?.toLowerCase().includes('inactive')) return { bg: '#ffeaea', text: '#d32f2f' };
    return { bg: '#f5f5f5', text: '#666' };
  };

  const getDataLayerTagStyle = (dataLayer) => {
    if (!dataLayer) return {};
    
    // Handle assetSource tags
    if (dataLayer === 'assetSource') {
      return {
        backgroundColor: '#f0f8ff',
        borderColor: '#bb1ccc',
        color: '#bb1ccc'
      };
    }
    
    switch (dataLayer.toLowerCase()) {
      case 'bronze':
        return {
          backgroundColor: '#CD7F32',
          color: 'white',
          borderColor: '#CD7F32'
        };
      case 'gold':
        return {
          backgroundColor: '#FFD700',
          color: '#1f2937',
          borderColor: '#FFD700'
        };
      case 'silver':
        return {
          backgroundColor: '#C0C0C0',
          color: '#1f2937',
          borderColor: '#C0C0C0'
        };
      case 'ingestion':
        return {
          backgroundColor: '#10B981',
          color: 'white',
          borderColor: '#10B981'
        };
      default:
        return {
          backgroundColor: '#f0f8ff',
          borderColor: '#bb1ccc',
          color: '#bb1ccc'
        };
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Typography 
        variant="body2" 
        sx={{ 
          mb: 1, 
          fontWeight: 600,
          fontSize: '14px',
          color: '#666'
        }}
      >
        {label}
      </Typography>
      
      {isLink ? (
        <Typography 
          variant="body2" 
          sx={{ 
            fontWeight: 600,
            color: '#bb1ccc',
            textDecoration: 'underline',
            cursor: 'pointer',
            fontSize: '14px'
          }}
          component="a"
          href={`mailto:${value}`}
        >
          {renderValue(value)}
        </Typography>
      ) : isStatus ? (
        <Box
          sx={{
            backgroundColor: getStatusColor(value).bg,
            color: getStatusColor(value).text,
            padding: '4px 12px',
            borderRadius: '6px',
            display: 'inline-block',
            fontSize: '13px',
            fontWeight: 600
          }}
        >
          {renderValue(value)}
        </Box>
      ) : isTag ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {Array.isArray(value) ? value.map((item, index) => (
            <Chip 
              key={index} 
              label={renderValue(item)} 
              size="small" 
              variant="outlined"
              sx={{
                ...getDataLayerTagStyle(customTagClass || item),
                fontSize: '12px',
                fontWeight: 500
              }}
            />
          )) : (
            <Chip 
              label={renderValue(value)} 
              size="small" 
              variant="outlined"
              sx={{
                ...getDataLayerTagStyle(customTagClass || value),
                fontSize: '12px',
                fontWeight: 500
              }}
            />
          )}
        </Box>
      ) : (
        <Typography 
          variant="body2" 
          sx={{ 
            fontWeight: 600,
            fontSize: '14px',
            color: '#333'
          }}
        >
          {renderValue(value)}
        </Typography>
      )}
    </Box>
  );
};

const ProductDetail = ({ product }) => {
  if (!product) return null;

  const getDataLayerClass = (dataLayer) => {
    if (!dataLayer) return '';
    switch (dataLayer.toLowerCase()) {
      case 'bronze':
        return 'bronze';
      case 'gold':
        return 'gold';
      case 'silver':
        return 'silver';
      case 'ingestion':
        return 'ingestion';
      default:
        return '';
    }
  };

  const getStatusColor = (status) => {
    if (status?.toLowerCase().includes('onboarded')) return { bg: '#e8f5e8', text: '#2e7d32' };
    if (status?.toLowerCase().includes('active')) return { bg: '#e8f5e8', text: '#2e7d32' };
    if (status?.toLowerCase().includes('inactive')) return { bg: '#ffeaea', text: '#d32f2f' };
    return { bg: '#f5f5f5', text: '#666' };
  };

  const getDataLayerTagStyle = (dataLayer) => {
    if (!dataLayer) return {};
    
    // Handle assetSource tags
    if (dataLayer === 'assetSource') {
      return {
        backgroundColor: '#f0f8ff',
        borderColor: '#bb1ccc',
        color: '#bb1ccc'
      };
    }
    
    switch (dataLayer.toLowerCase()) {
      case 'bronze':
        return {
          backgroundColor: '#CD7F32',
          color: 'white',
          borderColor: '#CD7F32'
        };
      case 'gold':
        return {
          backgroundColor: '#FFD700',
          color: '#1f2937',
          borderColor: '#FFD700'
        };
      case 'silver':
        return {
          backgroundColor: '#C0C0C0',
          color: '#1f2937',
          borderColor: '#C0C0C0'
        };
      case 'ingestion':
        return {
          backgroundColor: '#10B981',
          color: 'white',
          borderColor: '#10B981'
        };
      default:
        return {
          backgroundColor: '#f0f8ff',
          borderColor: '#bb1ccc',
          color: '#bb1ccc'
        };
    }
  };

  const basicDetailsData = [
    { label: 'Description', value: product.productDescription },
    { label: 'VALUE MAPPING', value: product.valueMapping || 'N/A' },
    { label: 'Data Layer', value: product.dataLayer, isTag: true, customTagClass: getDataLayerClass(product.dataLayer) },
    { label: 'Line of Business', value: product.lineOfBusiness === "mw" ? "MW" : product.lineOfBusiness === "health" ? "Health and Wellness" : product.lineOfBusiness === "immersive" ? "Immersive Experience" : product.lineOfBusiness },
    { label: 'Market Region', value: product.marketRegion, isTag: true },
    { label: 'Market Country', value: product.marketCountry, isTag: true },
    { label: 'Product Owner', value: product.productOwner, isLink: true },
    { label: 'Product Tags', value: product.productTag, isTag: true },
    { label: 'Parent Domain', value: product.parentDomain },
    { label: 'Domain', value: product.domain },
    { label: 'Sub Domain', value: product.subDomain },
    { label: 'Status', value: product.status, isStatus: true },
    { label: 'SLA UPTIME', value: product.slaUptime ? `${product.slaUptime}%` : 'N/A' }
  ];

  const technicalDetailsData = [
    { label: 'Size Threshold', value: product.sizeThreshold },
    { label: 'SLA Response Time', value: product.slaResponseTime },
    { label: 'Asset Sources', value: product.assetSource, isTag: true, customTagClass: 'assetSource' },
    { label: 'Min Expected Users', value: product.minExpectedUsers },
    { label: 'Max Expected Users', value: product.maxExpectedUsers }
  ];

  const renderTableCellValue = (item) => {
    if (!item.value || (Array.isArray(item.value) && item.value.length === 0)) return 'N/A';

    // Handle object values with value and unit properties
    if (item.value && typeof item.value === 'object' && item.value.value !== undefined && item.value.unit !== undefined) {
      return (
        <Typography 
          variant="body2" 
          sx={{ 
            fontWeight: 600,
            fontSize: '14px',
            color: '#333'
          }}
        >
          {item.value.value} {item.value.unit}
        </Typography>
      );
    }

    if (item.isLink) {
      return (
        <Typography 
          variant="body2" 
          sx={{ 
            fontWeight: 600,
            color: '#bb1ccc',
            textDecoration: 'underline',
            cursor: 'pointer',
            fontSize: '14px'
          }}
          component="a"
          href={`mailto:${item.value}`}
        >
          {item.value}
        </Typography>
      );
    }

    if (item.isStatus) {
      const statusColors = getStatusColor(item.value);
      return (
        <Box
          sx={{
            backgroundColor: statusColors.bg,
            color: statusColors.text,
            padding: '4px 12px',
            borderRadius: '6px',
            display: 'inline-block',
            fontSize: '13px',
            fontWeight: 600
          }}
        >
          {item.value}
        </Box>
      );
    }

    if (item.isTag) {
      // Helper function to capitalize first letter of data layer values
      const formatDataLayerValue = (val) => {
        if (typeof val === 'string' && ['bronze', 'gold', 'silver', 'ingestion'].includes(val.toLowerCase())) {
          return val.charAt(0).toUpperCase() + val.slice(1).toLowerCase();
        }
        return val;
      };

      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {Array.isArray(item.value) ? item.value.map((val, index) => (
            <Chip 
              key={index} 
              label={formatDataLayerValue(val)} 
              size="small" 
              variant="outlined"
              sx={{
                ...getDataLayerTagStyle(item.customTagClass || val),
                fontSize: '12px',
                fontWeight: 500
              }}
            />
          )) : (
            <Chip 
              label={formatDataLayerValue(item.value)} 
              size="small" 
              variant="outlined"
              sx={{
                ...getDataLayerTagStyle(item.customTagClass || item.value),
                fontSize: '12px',
                fontWeight: 500
              }}
            />
          )}
        </Box>
      );
    }

    return (
      <Typography 
        variant="body2" 
        sx={{ 
          fontWeight: 600,
          fontSize: '14px',
          color: '#333'
        }}
      >
        {item.value}
      </Typography>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Basic Details Section */}
      <Card sx={{ mb: 4, borderRadius: 2 }}>
        <Box sx={{ 
          borderBottom: '2px solid #9B00FB', 
          p: 3, 
          backgroundColor: '#9B00FB'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'white' }}>
            Basic Details
          </Typography>
        </Box>
        <CardContent sx={{ p: 3 }}>
          <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, backgroundColor: '#f5f5f5', fontSize: '14px' }}>
                    Field
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, backgroundColor: '#f5f5f5', fontSize: '14px' }}>
                    Value
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {basicDetailsData.map((item, index) => (
                  <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}>
                    <TableCell sx={{ fontWeight: 600, color: '#666', fontSize: '14px', width: '30%' }}>
                      {item.label}
                    </TableCell>
                    <TableCell sx={{ fontSize: '14px' }}>
                      {renderTableCellValue(item)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {product.productDocumentationUrl && (
            <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #e5e7eb' }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 1, 
                  fontWeight: 600,
                  fontSize: '14px',
                  color: '#666'
                }}
              >
                Documentation
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: 600,
                  color: '#bb1ccc',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
                component="a"
                href={product.productDocumentationUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                View Documentation
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Technical Details Section */}
      <Card sx={{ borderRadius: 2 }}>
        <Box sx={{ 
          borderBottom: '2px solid #FF8300', 
          p: 3, 
          backgroundColor: '#FF8300'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'white' }}>
            Technical Details
          </Typography>
        </Box>
        <CardContent sx={{ p: 3 }}>
          <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, backgroundColor: '#f5f5f5', fontSize: '14px' }}>
                    Field
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, backgroundColor: '#f5f5f5', fontSize: '14px' }}>
                    Value
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {technicalDetailsData.map((item, index) => (
                  <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}>
                    <TableCell sx={{ fontWeight: 600, color: '#666', fontSize: '14px', width: '30%' }}>
                      {item.label}
                    </TableCell>
                    <TableCell sx={{ fontSize: '14px' }}>
                      {renderTableCellValue(item)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ProductDetail;
